import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_SearchRow,
    WB_SearchProfileRow,
    WB_SearchProfileCRRow, WB_SearchProfileFCRRow, WB_SearchProfileGCRRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbSearch} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Search, esWB_Search_Profile, esWB_Search_ProfileCR, esWB_Search_ProfileFCR, esWB_Search_ProfileGCR
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {ASync} from "../PA.Core/Basic/ASync";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {SearchBOValidation} from "./SearchBO.Validation";
import {PAEntity} from "../PA.Core/PAEntity";
import {IColumnSchema, ISchema} from "../PA.Core/PA.Core.IF.Entity";
import {BehaviorSubject} from "rxjs";

export interface IQueryColumnOptions {
    columnID: string,
    sortOrder: number,
    dbType: string
    alignment?: string
}

export interface IFilterColumnOptions {
    columnID: string,
    sortOrder: number,
    dbType: string
    alignment?: string
}

export interface IGridColumnOptions {
    columnID?: string
}

export class SearchBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Search: WB_SearchRow;

    private _QueryColumnsChanged: BehaviorSubject<SearchBO>

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Search'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Search = undefined;
        this._QueryColumnsChanged = new BehaviorSubject<SearchBO>(undefined)
    }

    get WB_Search(): WB_SearchRow {
        return this.drWB_Search;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        var self = this;
        this.LoadProgressProvider.start("Creating Search");
        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == undefined) {
                var sError = "Unable to determine dbContext for new Search.";
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Search)
                .then((newSearchRow: WB_SearchRow) => {
                    newSearchRow.setDefaults()
                    self.drWB_Search = newSearchRow;
                    self.dbContext.add(esWB_Search, newSearchRow)
                    self.Identity.setAll(self.WB_Search.cif.WB_SEARCH_ID, self.WB_Search.cif.SEARCH_ID, self.WB_Search.cif.SEARCH_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Search, 'WB_SEARCH_ID', 'SEARCH_ID', 'SEARCH_NAME')

                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = undefined;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        return this._OnLoadComplete;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        var self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {

            self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

            if (self.bIsLoaded) {
                var sError = "Search already loaded.";
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == undefined) {
                var sError = "Unable to determine dbContext for new Search.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }
            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = SearchBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = SearchBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let aSearchRow: any = recordResolver.get(esWB_Search, anId);

                    if (_.isEmpty(aSearchRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_Search = aSearchRow;

                    self.Identity.setAll(self.WB_Search.cif.WB_SEARCH_ID, self.WB_Search.cif.SEARCH_ID, self.WB_Search.cif.SEARCH_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_Search, "WB_SEARCH_ID", "SEARCH_ID", "SEARCH_NAME");
                    self._bIsLoaded = true;
                })
                .then(() => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch(function (err: any) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_Search.cif.WB_SEARCH_ID);
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == null) {
                var sError = "Unable to determine dbContext for to Validate Search.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            SearchBOValidation.Validate(self)
                .then((ignore: any) => {
                    self._bIsValidated = self.ValidateMessageProvider.countMessages(MessageType.Error) <= 0;
                    self.ValidateProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);
                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbDomain = <IwbSearch>self.drWB_Search;
        this.SaveProgressProvider.start("Saving " + wbDomain.SEARCH_ID);

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (this.dbContext == null) {
                self._bIsSaved = false;
                var sError = "Unable to determine dbContext to Save Search.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }
            self.SaveOptions = options || {};

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            let bDone = false;
            self.Validate({})
                .then((gnore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false;
                        bDone = true;
                        self.SaveProgressProvider.finish();
                        resolve(self);
                        return;
                    }
                    return saveFunction(esWB_Search, self.drWB_Search, {})
                        .then((ignore) => {
                            var tasks: Promise<any>[] = []

                            tasks.push(saveFunction(esWB_Search_Profile, self.drWB_Search.WB_SearchProfileRows))

                            _.forEach(self.drWB_Search.WB_SearchProfileRows, (aRow: WB_SearchProfileRow) => {
                                tasks.push(saveFunction(esWB_Search_ProfileCR, aRow.WB_SearchProfileCRRows))
                                tasks.push(saveFunction(esWB_Search_ProfileFCR, aRow.WB_SearchProfileFCRRows))
                                tasks.push(saveFunction(esWB_Search_ProfileGCR, aRow.WB_SearchProfileGCRRows))
                            })

                            return Promise.all(tasks)
                        })
                        .then((ignored) => {
                            if (bBatch) {
                                self.dbContext.addBatchCompleteListener(() => {
                                    self._bIsSaved = true;
                                })
                            } else {
                                self._bIsSaved = true;
                            }
                            self.SaveProgressProvider.finish();
                            resolve(self);
                        })
                        .catch((err) => {
                            self.SaveProgressProvider.exception(err);
                            resolve(self)
                        })
                })
        });
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbSearch = <IwbSearch>self.drWB_Search;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbSearch.WB_SEARCH_ID);
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Domain

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Search.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc = "populate from SQL">

    async populateFromQuery(wbProfileId: number): Promise<void> {
        var self = this

        let aProfileRow: WB_SearchProfileRow = _.first(_.filter(self.WB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))
        let sSQL: string = aProfileRow.cif.SQL_QUERY

        let entity: PAEntity = new PAEntity()
        try {
            self.ValidateMessageProvider.removeMessage("InvalidSQL")
            let query = sSQL.replace(/union/gi, 'UNIONALL')
            let sqlSchema: ISchema = await entity.getSQLSchema(0, query)
            let columns = sqlSchema.properties

            await self.syncSearchProfileColumns(wbProfileId, columns)
        } catch (err) {
            self.ValidateMessageProvider.addMessage(
                {
                    code: "InvalidSQL",
                    type: MessageType.Error,
                    title: "Invalid SQL",
                    description: `Error: ${JSON.stringify(err, null, '\t')}`,
                    subtitle: "",
                    count: 0
                });
        }
    }

    async syncSearchProfileColumns(wbProfileId: number, arrColumns: { [column_name: string]: IColumnSchema; }): Promise<void> {
        const self = this

        let profileRow: WB_SearchProfileRow = _.first(_.filter(self.WB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))
        let dctColumns_Missing = _.clone(arrColumns)

        let columnNames: string[] = _.map(arrColumns, (aCol, key) => key)

        let dctColumns_Existing: { [columnId: string]: string } = {}

        _.forEach(profileRow.WB_SearchProfileCRRows, (aRow: WB_SearchProfileCRRow) => {
            if (!aRow.isActive)
                return

            let columnId: string = aRow.cif.COLUMN_ID

            // Remove Dups
            if (columnId in dctColumns_Existing) {
                aRow.markDeleted()
                return
            }

            // Track Existing
            dctColumns_Existing[columnId] = columnId

            if (!(columnId in arrColumns)) {
                aRow.markDeleted()
                return
            } else {
                aRow.cif.SORT_ORDER = columnNames.indexOf(columnId) + 1
            }

            delete dctColumns_Missing[columnId]

        })

        // Add Missing
        for (let key in dctColumns_Missing) {
            let fieldType = dctColumns_Missing[key].type
            let alignment = (fieldType == "INT") ? "Right" : "Left"
            await self.addProfileCR(wbProfileId, {
                columnID: key,
                sortOrder: columnNames.indexOf(key) + 1,
                dbType: dctColumns_Missing[key].type,
                alignment: alignment
            })
        }

    }

    //</editor-fold>

    //<editor-fold desc = "Additional operations">
    get QueryColumnsChanged(): BehaviorSubject<SearchBO> {
        return this._QueryColumnsChanged
    }

    addProfile(): Promise<IBusinessObject> {

        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            let aProfileRow: WB_SearchProfileRow = undefined
            let activeSearchProfiles = _.filter(self.WB_Search.WB_SearchProfileRows, aRow => aRow.isActive)

            self.dbContext.createRecordWithKey(esWB_Search_Profile)
                .then((aProfileRow: WB_SearchProfileRow) => {

                    aProfileRow.setDefaults()
                    aProfileRow.cif.WB_SEARCH_ID = self.drWB_Search.cif.WB_SEARCH_ID

                    if (activeSearchProfiles.length == 0) {
                        aProfileRow.cif.DEFAULT_IND = '1'
                        aProfileRow.cif.SORT_ORDER = 0
                    } else {
                        let maxSortOrder = _.maxBy(activeSearchProfiles, aRow => aRow.cif.SORT_ORDER).cif.SORT_ORDER
                        aProfileRow.cif.SORT_ORDER = maxSortOrder + 1
                    }


                    self.dbContext.add(esWB_Search_Profile, aProfileRow)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })
        })
    }

    removeProfile(wbProfileId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_Search.WB_SearchProfileRows, (aProfileRow: WB_SearchProfileRow) => {
                if (aProfileRow.isActive && aProfileRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId) {
                    aProfileRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    async addProfileCR(wbProfileId: number, columnOptions: IQueryColumnOptions): Promise<IBusinessObject> {

        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            let profileRow = _.first(_.filter(self.WB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))
            let checkExisting = _.filter(profileRow.WB_SearchProfileCRRows, aCRRow => aCRRow.isActive && aCRRow.cif.COLUMN_ID == columnOptions.columnID)
            if (checkExisting.length > 0) {
                resolve(self)
                return
            }

            self.dbContext.createRecordWithKey(esWB_Search_ProfileCR)
                .then((aProfileCRRow: WB_SearchProfileCRRow) => {

                    aProfileCRRow.setDefaults()
                    aProfileCRRow.cif.WB_SEARCH_PROFILE_ID = wbProfileId
                    aProfileCRRow.cif.COLUMN_ID = columnOptions.columnID
                    aProfileCRRow.cif.COLUMN_NAME = columnOptions.columnID
                    aProfileCRRow.cif.SORT_ORDER = columnOptions.sortOrder
                    aProfileCRRow.cif.COLUMN_DATATYPE = columnOptions.dbType
                    aProfileCRRow.cif.ALIGNMENT = columnOptions.alignment

                    self.dbContext.add(esWB_Search_ProfileCR, aProfileCRRow)
                    self.QueryColumnsChanged.next(this)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })
        })
    }

    removeProfileCR(wbProfileId: number, wbProfileCRId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            let profileRow: WB_SearchProfileRow = _.first(_.filter(self.drWB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))

            _.forEach(profileRow.WB_SearchProfileCRRows, (aProfileCRRow: WB_SearchProfileCRRow) => {
                if (aProfileCRRow.isActive && aProfileCRRow.cif.WB_SEARCH_PROFILECR_ID == wbProfileCRId) {
                    aProfileCRRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    async addProfileFCR(wbProfileId: number): Promise<IBusinessObject> {

        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            let searchProfile = _.first(_.filter(self.WB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))
            let activeSearchFCR = _.filter(searchProfile.WB_SearchProfileFCRRows, aRow => aRow.isActive)
            let maxSortOrder = 0
            if (activeSearchFCR.length > 0) {
                maxSortOrder = _.maxBy(activeSearchFCR, aRow => aRow.cif.SORT_ORDER).cif.SORT_ORDER
            }

            self.dbContext.createRecordWithKey(esWB_Search_ProfileFCR)
                .then((aProfileFCRRow: WB_SearchProfileCRRow) => {

                    aProfileFCRRow.setDefaults()
                    aProfileFCRRow.cif.WB_SEARCH_PROFILE_ID = wbProfileId
                    aProfileFCRRow.cif.SORT_ORDER = maxSortOrder + 1

                    self.dbContext.add(esWB_Search_ProfileFCR, aProfileFCRRow)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })
        })
    }

    removeProfileFCR(wbProfileId: number, wbProfileFCRId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            let profileRow: WB_SearchProfileRow = _.first(_.filter(self.drWB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))

            _.forEach(profileRow.WB_SearchProfileFCRRows, (aProfileFCRRow: WB_SearchProfileFCRRow) => {
                if (aProfileFCRRow.isActive && aProfileFCRRow.cif.WB_SEARCH_PROFILEFCR_ID == wbProfileFCRId) {
                    aProfileFCRRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    async addProfileGCR(wbProfileId: number, columnOptions: IGridColumnOptions): Promise<IBusinessObject> {

        var self = this

        let opts: IGridColumnOptions = columnOptions || {};

        return new Promise<IBusinessObject>((resolve, error) => {

            let searchProfile = _.first(_.filter(self.WB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))
            let activeSearchGCR = _.filter(searchProfile.WB_SearchProfileGCRRows, aRow => aRow.isActive)
            let maxSortOrder = 0
            if (activeSearchGCR.length > 0) {
                maxSortOrder = _.maxBy(activeSearchGCR, aRow => aRow.cif.SORT_ORDER).cif.SORT_ORDER
            }

            if ('columnID' in opts) {
                let checkExisting = _.filter(searchProfile.WB_SearchProfileGCRRows, aRow => aRow.isActive && aRow.cif.COLUMN_ID == opts.columnID)
                if (checkExisting.length > 0) {
                    resolve(self)
                    return
                }
            }

            self.dbContext.createRecordWithKey(esWB_Search_ProfileGCR)
                .then((aProfileGCRRow: WB_SearchProfileGCRRow) => {

                    aProfileGCRRow.setDefaults()
                    aProfileGCRRow.cif.WB_SEARCH_PROFILE_ID = wbProfileId
                    aProfileGCRRow.cif.SORT_ORDER = maxSortOrder + 1
                    aProfileGCRRow.cif.UI_COLUMN_TYPE = "Field"

                    if ('columnID' in opts)
                        aProfileGCRRow.cif.COLUMN_ID = opts.columnID;

                    self.dbContext.add(esWB_Search_ProfileGCR, aProfileGCRRow)
                    resolve(self)
                }).catch((err) => {
                error(err)
            })
        })
    }

    async removeProfileGCR(wbProfileId: number, wbProfileGCRId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {
            let profileRow: WB_SearchProfileRow = _.first(_.filter(self.drWB_Search.WB_SearchProfileRows, aRow => aRow.cif.WB_SEARCH_PROFILE_ID == wbProfileId))

            _.forEach(profileRow.WB_SearchProfileGCRRows, (aProfileGCRRow: WB_SearchProfileGCRRow) => {
                if (aProfileGCRRow.isActive && aProfileGCRRow.cif.WB_SEARCH_PROFILEGCR_ID == wbProfileGCRId) {
                    aProfileGCRRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

    //</editor-fold>


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const business_unit_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, business_unit_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Search, ids)
                .then((result) => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                })
        })
    }
};

PAEnvironment.registerBusinessObjectClass(SearchBO, "SearchBO", "Search")